import { useCallback, useEffect, useRef } from 'react';

import { isNectarConsoleEnabled } from '~/devtools/isNectarConsoleEnabled';
import { recordBatch as recordDevEventBatch } from '~/devtools/nectarEventLog';
import { getNectarVisitToken, getOrCreateAnonymousUserId, isPwaDisplayMode } from '~/utils/nectarTracking';
import { hashSha256 } from '~/utils/sha256';
import { generateUUID } from '~/utils/uuid';

import { VISIT_TOKEN_READY_EVENT_NAME } from './useLoaders/useCreateVisitToken';
import { useNonRevalidatingFetcher } from './useNonRevalidatingFetcher';

const buildEmailHashSha256 = (email?: string): string | undefined => {
  if (!email) return undefined;
  try {
    return hashSha256(email.toLowerCase());
  } catch {
    return undefined;
  }
};

// Sanitizes a URL by replacing jwt_token query parameter with a placeholder
const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    if (urlObj.searchParams.has('jwt_token')) {
      urlObj.searchParams.set('jwt_token', 'REDACTED');
    }
    return urlObj.toString();
  } catch {
    return url;
  }
};

// Enum for analytics event types
export enum AnalyticsEventType {
  PAGEVIEW = 'pageview',
  CLICK = 'click',
  // Generic action verbs disambiguated by dimensions (sendable_type, page_block,
  // community_id), not feature-prefixed values. An entity being surfaced (a post in
  // the feed, the upgrade modal) is an `impression`; page_block/sendable_type say which.
  IMPRESSION = 'impression',
  SEARCH = 'search',
  ONBOARDING_COMPLETED = 'onboarding_completed',
}

// Types for analytics events
interface AnalyticsEvent {
  user_agent: string;
  url: string;
  event: AnalyticsEventType;
  client_timestamp: number;
  event_id: string;
  nectar_id: string;
  publication_id: string;
  visit_token: string;
  ip: string;
  store_ip: boolean;
  url_referrer?: string;
  click_type?: string;
  click_url?: string;
  click_raw_tag_name?: string;
  page_block?: string;
  community_id?: string;
  community_space_id?: string;
  sendable_id?: string;
  sendable_type?: string;
  subscription_id?: number;
  email_hash_sha256?: string;
  anonymous_user_id: string;
  is_pwa?: boolean;
  site_id?: string;
}

export interface NectarAnalyticsSettings {
  batchSize: number;
  batchTimeoutMs: number;
  pageViewTracking: boolean;
  clickTracking: boolean;
  ipTracking: boolean;
}

interface UseNectarAnalyticsProps {
  nectarId: string;
  enabled: boolean;
  publicationId: string;
  siteId?: string;
  postId?: string;
  communityId?: string;
  communitySpaceId?: string;
  subscriptionId?: number;
  email?: string;
  settings: NectarAnalyticsSettings;
}

interface TrackImpressionParams {
  sendableId?: string;
  sendableType?: string;
  pageBlock: string;
  communitySpaceId?: string;
}

interface UseNectarAnalyticsReturn {
  trackPageView: () => void;
  trackClick: (clickData: {
    clickType: string;
    clickUrl?: string;
    clickRawTagName?: string;
    pageBlock?: string;
  }) => void;
  trackImpression: (params: TrackImpressionParams) => void;
}

// Hook for tracking analytics events
export const useNectarAnalytics = ({
  nectarId,
  enabled,
  publicationId,
  siteId,
  postId,
  communityId,
  communitySpaceId,
  subscriptionId,
  email,
  settings,
}: UseNectarAnalyticsProps): UseNectarAnalyticsReturn => {
  const lastPageViewRef = useRef<string>('');
  const eventQueue = useRef<AnalyticsEvent[]>([]);
  const visitTokenPendingEventsRef = useRef<AnalyticsEvent[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const fetcher = useNonRevalidatingFetcher();

  // Extract visit token from existing visit_token cookie
  const extractVisitToken = useCallback((): string | null => {
    const token = getNectarVisitToken();
    return token || null;
  }, []);

  // Send batch to server
  const sendBatch = useCallback(
    (events: AnalyticsEvent[]): void => {
      if (isNectarConsoleEnabled()) {
        recordDevEventBatch(events);
      }

      fetcher.submit(
        JSON.stringify({
          events,
          settings,
        }),
        {
          method: 'post',
          action: '/nectar_analytics',
          encType: 'application/json',
        },
      );
    },
    [fetcher, settings],
  );

  // Process the event queue
  const processQueue = useCallback(() => {
    if (isProcessingRef.current || eventQueue.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    // Take up to batchSize events from the queue
    const eventsToProcess = eventQueue.current.splice(0, settings.batchSize);

    if (eventsToProcess.length === 0) {
      isProcessingRef.current = false;
      return;
    }

    sendBatch(eventsToProcess);

    isProcessingRef.current = false;

    // Schedule next processing if there are more events
    if (eventQueue.current.length > 0) {
      // Schedule processing after timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        processQueue();
      }, settings.batchTimeoutMs);
    }
  }, [settings.batchSize, settings.batchTimeoutMs, sendBatch]);

  // Schedule queue processing
  const scheduleProcessing = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      processQueue();
    }, settings.batchTimeoutMs);
  }, [processQueue, settings.batchTimeoutMs]);

  // Force flush the queue (useful for page unload, etc.)
  const flushQueue = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    processQueue();
  }, [processQueue]);

  // Send event to analytics endpoint
  const sendEvent = useCallback(
    (event: AnalyticsEvent): void => {
      eventQueue.current.push(event);

      // If we've reached the batch size, process immediately
      if (eventQueue.current.length >= settings.batchSize) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        processQueue();
      } else {
        // Otherwise, schedule processing after timeout
        scheduleProcessing();
      }
    },
    [processQueue, scheduleProcessing, settings.batchSize],
  );

  // Flush events that were pending for visit token
  const flushEventsPendingForVisitToken = useCallback((): void => {
    if (visitTokenPendingEventsRef.current.length === 0) {
      return;
    }

    const visitToken = extractVisitToken();
    if (!visitToken) {
      return;
    }

    // Process all events now that we have a visit token
    const pendingEvents = visitTokenPendingEventsRef.current.splice(0);
    pendingEvents.forEach((event) => {
      // Fill in the visit token for events that were created without it
      event.visit_token = visitToken;
      sendEvent(event as AnalyticsEvent);
    });
  }, [extractVisitToken, sendEvent]);

  // Create full event with current data (visit_token may be missing)
  const createEvent = useCallback(
    (params: {
      event_type: AnalyticsEventType;
      click_type?: string;
      click_url?: string;
      click_raw_tag_name?: string;
      page_block?: string;
      sendable_id?: string;
      sendable_type?: string;
      community_space_id?: string;
    }): AnalyticsEvent => {
      return {
        user_agent: navigator.userAgent,
        url: sanitizeUrl(window.location.href),
        event: params.event_type,
        client_timestamp: Date.now(),
        event_id: generateUUID(),
        nectar_id: nectarId,
        publication_id: publicationId,
        site_id: siteId,
        visit_token: extractVisitToken() || '', // Will be empty if not available
        ip: '', // Will be populated by server-side action
        store_ip: settings.ipTracking,
        url_referrer: document.referrer || undefined,
        sendable_id: params.sendable_id ?? postId,
        sendable_type: params.sendable_type ?? (postId ? 'post' : undefined),
        community_id: communityId,
        community_space_id: params.community_space_id ?? communitySpaceId,
        subscription_id: subscriptionId,
        email_hash_sha256: buildEmailHashSha256(email),
        anonymous_user_id: getOrCreateAnonymousUserId(),
        click_type: params.click_type,
        click_url: params.click_url,
        click_raw_tag_name: params.click_raw_tag_name,
        page_block: params.page_block ?? '',
        is_pwa: isPwaDisplayMode(),
      };
    },
    [
      extractVisitToken,
      nectarId,
      publicationId,
      siteId,
      settings.ipTracking,
      postId,
      communityId,
      communitySpaceId,
      subscriptionId,
      email,
    ],
  );

  // Queue event pending for visit token
  const queueEventPendingForVisitToken = useCallback((event: AnalyticsEvent): void => {
    visitTokenPendingEventsRef.current.push(event);
  }, []);

  // Track page view
  const trackPageView = useCallback((): void => {
    if (!settings.pageViewTracking) return;

    const currentUrl = window.location.href;

    // Don't track duplicate page views for the same URL
    if (currentUrl === lastPageViewRef.current) return;
    lastPageViewRef.current = currentUrl;

    const event = createEvent({ event_type: AnalyticsEventType.PAGEVIEW });

    // If no visit token, queue it; otherwise send immediately
    if (!event.visit_token) {
      queueEventPendingForVisitToken(event);
    } else {
      sendEvent(event);
    }
  }, [createEvent, sendEvent, settings.pageViewTracking, queueEventPendingForVisitToken]);

  // Track click event
  const trackClick = useCallback(
    ({
      clickType,
      clickUrl,
      clickRawTagName,
      pageBlock,
    }: {
      clickType: string;
      clickUrl?: string;
      clickRawTagName?: string;
      pageBlock?: string;
    }): void => {
      if (!settings.clickTracking) return;

      const event = createEvent({
        event_type: AnalyticsEventType.CLICK,
        click_type: clickType,
        click_url: clickUrl,
        click_raw_tag_name: clickRawTagName,
        page_block: pageBlock,
      });

      // If no visit token, queue it; otherwise send immediately
      if (!event.visit_token) {
        queueEventPendingForVisitToken(event);
      } else {
        sendEvent(event);
      }
    },
    [createEvent, sendEvent, settings.clickTracking, queueEventPendingForVisitToken],
  );

  // Track impression event (entity surfaced in the viewport). The entity and
  // surface are carried by dimensions: sendable_type/sendable_id + page_block.
  const trackImpression = useCallback(
    ({ sendableId, sendableType, pageBlock, communitySpaceId: spaceId }: TrackImpressionParams): void => {
      const event = createEvent({
        event_type: AnalyticsEventType.IMPRESSION,
        sendable_id: sendableId,
        sendable_type: sendableType,
        page_block: pageBlock,
        community_space_id: spaceId,
      });

      // If no visit token, queue it; otherwise send immediately
      if (!event.visit_token) {
        queueEventPendingForVisitToken(event);
      } else {
        sendEvent(event);
      }
    },
    [createEvent, sendEvent, queueEventPendingForVisitToken],
  );

  // Listen for visitTokenReady event
  useEffect(() => {
    const handleVisitTokenReady = () => {
      flushEventsPendingForVisitToken();
    };

    window.addEventListener(VISIT_TOKEN_READY_EVENT_NAME, handleVisitTokenReady);

    return () => {
      window.removeEventListener(VISIT_TOKEN_READY_EVENT_NAME, handleVisitTokenReady);
    };
  }, [flushEventsPendingForVisitToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Flush queue on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushQueue();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushQueue]);

  // Early return with no-op methods if analytics is disabled
  if (!enabled) {
    return {
      trackPageView: () => {},
      trackClick: () => {},
      trackImpression: () => {},
    };
  }

  return {
    trackPageView,
    trackClick,
    trackImpression,
  };
};

export type { AnalyticsEvent, UseNectarAnalyticsProps, UseNectarAnalyticsReturn };
